import { Component, Input, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WebsocketService } from '../../../services/websocket.service';
import { UniChannelCardComponent } from './uni-channel-card.component';
import { TranslatePipe } from '@ngx-translate/core';

// Dashboard for a uni-family controller (water / heater / fan). Renders one card per
// channel and keeps telemetry live off the UNITELEMETRY stream. The controller object
// is the UniControllerDTO the cloud already sent in USERDEVICES (type === 'uniController').
//
// telemetry shape (patched in place): { "<channelId>": { "<PROPERTY>": value } }.
@Component({
  selector: 'app-uni-dashboard',
  standalone: true,
  imports: [TranslatePipe, CommonModule, FormsModule, UniChannelCardComponent],
  templateUrl: './uni-dashboard.component.html',
  styleUrls: ['./uni-dashboard.component.scss'],
})
export class UniDashboardComponent implements OnInit, OnDestroy {
  @Input() controller: any;
  @Input() canConfigure = false;

  private ws = inject(WebsocketService);
  private destroyRef = inject(DestroyRef);

  editing = false;
  scanGpio: number | null = null;
  scannedRoms: string[] = [];

  ngOnInit(): void {
    if (!this.controller.telemetry) this.controller.telemetry = {};

    // Live telemetry: patch the per-channel value map in place so the cards re-render.
    this.ws.on('UNITELEMETRY').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((m: any) => {
      if (!m || m.mac !== this.controller.mac) return;
      const t = { ...(this.controller.telemetry || {}) };
      for (const u of m.updates || []) {
        const ch = { ...(t[u.channelId] || {}) };
        ch[u.property] = u.value;
        t[u.channelId] = ch;
      }
      this.controller.telemetry = t;
    });

    // Channels/config-date after an edit or a device-side adopt.
    this.ws.on('UNICHANNELS').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((m: any) => {
      if (!m || m.mac !== this.controller.mac) return;
      this.controller.channels = m.channels;
      this.controller.configDate = m.configDate;
      this.controller.deviceConfigDate = m.deviceConfigDate;
      this.editing = false;
    });

    this.ws.on('UNISCANRESULT').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((m: any) => {
      if (!m || m.mac !== this.controller.mac) return;
      this.scannedRoms = m.roms || [];
    });
  }

  ngOnDestroy(): void {}

  get channels(): any[] {
    return this.controller?.channels || [];
  }

  // The device is not running what the cloud holds — same banner logic as relay controllers.
  get configDirty(): boolean {
    const c = this.controller?.configDate, d = this.controller?.deviceConfigDate;
    return !!c && (!d || new Date(c).getTime() !== new Date(d).getTime());
  }

  telemetryFor(channelId: number): any {
    return this.controller?.telemetry?.[channelId] || {};
  }

  // A channel drove one of its controls (fan slider, boiler target, pump switch).
  onCommand(ev: { channelId: number; cmd: string; value: number }): void {
    this.ws.sendMessage({ type: 'UNICOMMAND', payload: { mac: this.controller.mac, ...ev } });
  }

  uploadConfig(): void {
    this.ws.sendMessage({ type: 'COMMAND', payload: { mac: this.controller.mac, command: 'UPLOADCONFIG' } });
  }

  saveChannels(channels: any[]): void {
    this.ws.sendMessage({ type: 'UPDATEUNICHANNELS', payload: { mac: this.controller.mac, channels } });
  }

  scan(): void {
    if (this.scanGpio == null) return;
    this.scannedRoms = [];
    this.ws.sendMessage({ type: 'UNISCAN', payload: { mac: this.controller.mac, gpio: this.scanGpio } });
  }
}
